import { useState, useMemo } from "react";
import { useGetList } from "ra-core";
import { Check, ChevronsUpDown, MapPin, User, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type {
  Job,
  Contact,
} from "../../providers/fakerest/dataGenerator/types";

interface JobSearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  onCreateNew?: () => void;
}

export const JobSearchSelect = ({
  value,
  onChange,
  placeholder = "Select a job...",
  disabled = false,
  onCreateNew,
}: JobSearchSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Fetch jobs
  const { data: jobs } = useGetList<Job>("jobs", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "created_at", order: "DESC" },
  });

  // Fetch contacts to get names
  const { data: contacts } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 500 },
  });

  // Create contact lookup map
  const contactMap = useMemo(() => {
    if (!contacts) return {};
    return contacts.reduce(
      (acc, contact) => {
        acc[contact.id] = contact.name;
        return acc;
      },
      {} as Record<number, string>,
    );
  }, [contacts]);

  // Filter jobs based on search
  const filteredJobs = useMemo(() => {
    if (!jobs) return [];
    if (!search) return jobs;

    const searchLower = search.toLowerCase();
    return jobs.filter((job) => {
      const contactName = contactMap[job.contact_id] || "";
      return (
        job.address.toLowerCase().includes(searchLower) ||
        contactName.toLowerCase().includes(searchLower)
      );
    });
  }, [jobs, search, contactMap]);

  // Find selected job
  const selectedJob = useMemo(() => {
    if (!value || !jobs) return null;
    return jobs.find((job) => String(job.id) === value);
  }, [value, jobs]);

  const handleSelect = (jobId: string) => {
    onChange(jobId);
    setOpen(false);
    setSearch("");
  };

  const handleCreateNew = () => {
    setOpen(false);
    setSearch("");
    onCreateNew?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-auto min-h-10 py-2 font-normal"
          disabled={disabled}
        >
          {selectedJob ? (
            <div className="flex flex-col items-start text-left">
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{selectedJob.address}</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <User className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  {contactMap[selectedJob.contact_id] || "Unknown Contact"}
                </span>
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by address or contact..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {onCreateNew && (
              <CommandGroup>
                <CommandItem
                  onSelect={handleCreateNew}
                  className="py-2 text-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create new job
                </CommandItem>
              </CommandGroup>
            )}
            {onCreateNew && <CommandSeparator />}
            <CommandEmpty>No jobs found.</CommandEmpty>
            <CommandGroup>
              {filteredJobs.map((job) => (
                <CommandItem
                  key={job.id}
                  value={String(job.id)}
                  onSelect={() => handleSelect(String(job.id))}
                  className="py-2"
                >
                  {value === String(job.id) && (
                    <Check className="h-4 w-4 mr-2 shrink-0" />
                  )}
                  <div className="flex flex-col min-w-0">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate">{job.address}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <User className="h-3 w-3 shrink-0" />
                      <span className="truncate">
                        {contactMap[job.contact_id] || "Unknown Contact"}
                      </span>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
